import { Outlet, Link, useLocation } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'

const Layout = () => {
  const { user, logout } = useAuth()
  const location = useLocation()

  const isActive = (path) => {
    return location.pathname === path || location.pathname.startsWith(path + '/')
  }

  const isCOO = user?.role?.level === 2
  const isCEO = user?.role?.level === 1

  // Navigation items based on role
  const getNavItems = () => {
    const items = []

    // Dashboard Link - Role-specific
    items.push({
      to: isCEO ? "/ceo-dashboard" : isCOO ? "/coo-dashboard" : "/dashboard",
      label: "Dashboard",
      shortLabel: "Home",
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
        </svg>
      ),
      isActive: (isCEO && isActive('/ceo-dashboard')) ||
                (isCOO && isActive('/coo-dashboard')) ||
                (!isCEO && !isCOO && isActive('/dashboard'))
    })

    if (!isCOO && !isCEO) {
      items.push(
        { to: "/kanban", label: "Kanban", shortLabel: "Kanban", icon: <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" /></svg>, isActive: isActive('/kanban') },
        { to: "/gyms", label: "Gyms", shortLabel: "Gyms", icon: <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>, isActive: isActive('/gyms') }
      )

      items.push(
        { to: "/pre-work-checklist", label: "Pre-Work", shortLabel: "Pre-Work", icon: <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" /></svg>, isActive: isActive('/pre-work-checklist') }
      )

      if (user?.role?.level <= 3) {
        items.push({ to: "/users", label: "Users", shortLabel: "Users", icon: <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" /></svg>, isActive: isActive('/users') })
      }

      if (user?.role?.level === 1 || user?.role?.level === 3) {
        items.push({ to: "/expired-clients", label: "Expired Clients", shortLabel: "Expired", icon: <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>, isActive: isActive('/expired-clients') })
      }
    }

    return items
  }

  const navItems = getNavItems()

  return (
    <div className="min-h-screen bg-gray-50 pb-16 sm:pb-0">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-14 sm:h-16">
            <div className="flex items-center">
              <h1 className="text-base sm:text-xl font-bold text-wtf-red">
                WTF Gym Acquisition
              </h1>
            </div>

            {/* Desktop Header Right Side */}
            <div className="hidden sm:flex items-center space-x-4">
              <span className="text-sm text-gray-700">
                Welcome, {user?.name}
              </span>
              <span className={`px-2 py-1 text-xs rounded-full ${
                user?.role?.level === 1 ? 'bg-red-100 text-red-800' :
                user?.role?.level === 2 ? 'bg-orange-100 text-orange-800' :
                user?.role?.level === 3 ? 'bg-green-100 text-green-800' :
                'bg-blue-100 text-blue-800'
              }`}>
                {user?.role?.name}
              </span>
              <button
                onClick={logout}
                className="btn-secondary text-sm"
              >
                Logout
              </button>
            </div>

            {/* Mobile Header Right Side */}
            <div className="flex sm:hidden items-center space-x-2">
              <span className={`px-2 py-1 text-xs rounded-full ${
                user?.role?.level === 1 ? 'bg-red-100 text-red-800' :
                user?.role?.level === 2 ? 'bg-orange-100 text-orange-800' :
                user?.role?.level === 3 ? 'bg-green-100 text-green-800' :
                'bg-blue-100 text-blue-800'
              }`}>
                {user?.role?.name}
              </span>
              <button
                onClick={logout}
                className="p-2 text-red-600 hover:bg-red-50 rounded-md"
                title="Logout"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Desktop Navigation */}
      <nav className="bg-white shadow-sm hidden sm:block sticky top-16 z-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex space-x-8 overflow-x-auto">
            {navItems.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className={`inline-flex items-center px-1 pt-4 pb-4 border-b-2 text-sm font-medium whitespace-nowrap ${
                  item.isActive
                    ? 'border-red-500 text-red-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {item.label}
              </Link>
            ))}
          </div>
        </div>
      </nav>

      {/* Mobile Horizontal Scrollable Navigation - Below Header */}
      <nav className="sm:hidden bg-white border-b border-gray-200 sticky top-14 z-20">
        <div className="flex overflow-x-auto scrollbar-hide px-2 py-2 space-x-1" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
          {navItems.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className={`flex-shrink-0 flex items-center px-3 py-2 rounded-full text-sm font-medium transition-colors ${
                item.isActive
                  ? 'bg-red-500 text-white shadow-sm'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200 active:bg-gray-300'
              }`}
            >
              <span className="mr-1.5">{item.icon}</span>
              <span className="whitespace-nowrap">{item.shortLabel}</span>
            </Link>
          ))}
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto py-4 sm:py-6 px-4 sm:px-6 lg:px-8">
        <div className="sm:px-0">
          <Outlet />
        </div>
      </main>
    </div>
  )
}

export default Layout
